import { view, extensions, linkEditing, cssGenerator } from '@yola/ws-sdk';
import buildLinkElement from 'src/js/modules/extensions/common/helpers/build-link-element';
import buildPictureElement from 'src/js/modules/extensions/common/helpers/build-picture-element';
import mediaAttributes from 'src/js/modules/extensions/common/constants/media-attributes';
import linkAttributes from 'src/js/modules/extensions/common/constants/link-attributes';
import { createWsIframeElement } from 'src/js/modules/extensions/registry/ws-iframe/helpers/create-iframe-element';
import constants from '../constants';
import mediaTypes from '../constants/media-types';

const { CONTENT_POSITION: CONTENT_POSITION_CLASS } = cssGenerator.constants.classNames;

const { linkTypes } = linkEditing.constants;

const MEDIA_CONTAINER_SELECTOR = 'ws-media-container';

const setGalleryItemCommonAttributes = (galleryItem, data) => {
  galleryItem.setAttribute('aspect-ratio', data.aspectRatio);
  galleryItem.setAttribute('content-scale', data.contentScale);
  galleryItem.setAttribute('media-group-id', data.mediaGroupId);
  galleryItem.setAttribute('class', `${CONTENT_POSITION_CLASS}-[${data.contentPosition}]`);

  if (data.isPlaceholder) {
    galleryItem.setAttribute(mediaAttributes.PLACEHOLDER_ATTRIBUTE, 'true');
  } else {
    galleryItem.removeAttribute(mediaAttributes.PLACEHOLDER_ATTRIBUTE);
  }

  if (data.lightboxDisabled) {
    galleryItem.setAttribute(mediaAttributes.LIGHTBOX_DISABLE_ATTRIBUTE, '');
  } else {
    galleryItem.removeAttribute(mediaAttributes.LIGHTBOX_DISABLE_ATTRIBUTE);
  }
};

const setGalleryItemAttributes = (galleryItem, data) => {
  galleryItem.setAttribute('origin-src', data.originSrc);
  galleryItem.setAttribute('origin-width', data.originWidth);
  galleryItem.setAttribute('origin-height', data.originHeight);
  setGalleryItemCommonAttributes(galleryItem, data);
};

const wrapWithLinkElement = (element, linkConfig) => {
  const newLinkElement = buildLinkElement(linkConfig);
  newLinkElement.classList.add(linkAttributes.CLICK_ACTION_LINK_CLASS);
  newLinkElement.appendChild(element);
  return newLinkElement;
};

const buildGalleryElementForImage = (templateElement, data) => {
  const galleryItemElement = templateElement.cloneNode(true);
  const galleryItem = galleryItemElement.matches(MEDIA_CONTAINER_SELECTOR)
    ? galleryItemElement
    : galleryItemElement.querySelector(MEDIA_CONTAINER_SELECTOR);

  setGalleryItemAttributes(galleryItem, data);
  galleryItem.innerHTML = '';

  const imageAttributes = {
    src: data.src,
    alt: data.alt,
    title: data.title,
    description: data.description,
  };
  const pictureElement = buildPictureElement(imageAttributes);

  let newElement = pictureElement;

  if (
    data.linkConfig &&
    data.linkConfig.linkType !== linkTypes.NO_ACTION &&
    data.linkConfig.linkType !== linkTypes.VIEW_FULL_SIZE_IMAGE
  ) {
    newElement = wrapWithLinkElement(pictureElement, data.linkConfig);
  }

  galleryItem.appendChild(newElement);

  return galleryItemElement;
};

const buildGalleryElementForIframe = (templateElement, data) => {
  const galleryItemElement = templateElement.cloneNode(true);
  const galleryItem = galleryItemElement.matches(MEDIA_CONTAINER_SELECTOR)
    ? galleryItemElement
    : galleryItemElement.querySelector(MEDIA_CONTAINER_SELECTOR);

  setGalleryItemCommonAttributes(galleryItem, data);
  galleryItem.innerHTML = '';
  const wsIframeElement = createWsIframeElement({
    ...data.iframeData,
    src: data.src,
  });
  galleryItem.appendChild(wsIframeElement);

  return galleryItemElement;
};

const buildGalleryElement = (templateElement, data) => {
  if (data.mediaType === mediaTypes.IFRAME) {
    return buildGalleryElementForIframe(templateElement, data);
  }

  return buildGalleryElementForImage(templateElement, data);
};

const buildGalleryElements = (galleryId, itemsData) => {
  const { galleryQuerySelector, sliderGalleryItemSelector } =
    extensions.accessors.getExtensionSettings(constants.common.slug);
  const targetLiveElement = view.accessors.getLiveElement(galleryId);
  const galleryWrapElement = targetLiveElement.querySelector(galleryQuerySelector);
  const galleryWrapLiveId = view.accessors.getLiveElementId(galleryWrapElement);
  const galleryWrapStaticElement = view.accessors.getStaticElement(galleryWrapLiveId);
  const galleryWrap = galleryWrapStaticElement.cloneNode(false);

  const galleryItemLiveElement = targetLiveElement.querySelector(sliderGalleryItemSelector);
  const galleryItemLiveId = view.accessors.getLiveElementId(galleryItemLiveElement);
  const galleryItemTemplateElement = view.accessors.getStaticElement(galleryItemLiveId);

  itemsData.forEach((itemData) => {
    const galleryItemElement = buildGalleryElement(galleryItemTemplateElement, itemData);
    galleryWrap.appendChild(galleryItemElement);
  });

  view.operations.replaceElement(galleryWrap, galleryWrapLiveId);
};

export default buildGalleryElements;
